import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  CardBox,
  CardBoxLabel,
  Chip,
  CircularProgress,
  EmptyState,
  IconButton,
  ModalBox,
  PageHeader,
  SearchIcon,
  TextField,
  Tooltip,
} from 'components';
import { useIsMobile } from 'hooks';
import {
  Calendar,
  Card,
  CloseCircle,
  DollarCircle,
  Filter,
  FilterRemove,
  Home2,
  Printer,
  PrinterSlash,
} from 'iconsax-react';

import { PaymentRequestForm } from './PaymentRequestForm';

import {
  ContentLoader,
  _,
  dayjs,
  i18n,
  styled,
  useNavigate,
  useParams,
  useSelector,
  useTranslation,
} from 'third-party';

import {
  PaymentColors,
  PaymentStatus,
  TOOLTIP_APPEAR_DELAY,
  USER_INPUT_DEBOUNCE_TIME_MS,
} from 'constants/common';
import {
  printAnonECheckUrl,
  userPaymentDetailsUrl,
  userPaymentOrganizationDetailsUrl,
} from 'constants/url';

import { usePayment } from 'modules/payment';

import { paymentSelectors } from 'selectors';

import {
  getOrganizationAvatarUrl,
  moneyFormatter,
  userCanDownloadCheck,
} from 'utils/app';

import { APIPayment, APIPaymentStatus, APISettingName } from 'types/api';

import { palette } from 'styles/theme';

const StyleInput = styled(TextField)`
  .MuiInput-input {
    padding: 0.4rem !important;
    min-width: 18rem;
  }
`;

const cardActions = (item: APIPayment) => {
  const eCheckPrintLimit = +(
    item.organization?.settings?.find(
      it => it.name === APISettingName.ECheckPrintLimit,
    )?.value || 1
  );

  const canUserDownloadCheck =
    userCanDownloadCheck(item) && item.ePrintCount < eCheckPrintLimit;

  return (
    <Box
      display="flex"
      width="100%"
      alignItems="center"
      justifyContent="space-between"
    >
      <Chip
        label={PaymentStatus[item.status as APIPaymentStatus]}
        style={{
          height: '1.5rem',
          padding: '0rem',
          marginLeft: '0.3rem',
          marginTop: '0.2rem',
          backgroundColor: PaymentColors[item.status as APIPaymentStatus],
        }}
      />
      {canUserDownloadCheck && (
        <Tooltip
          placement="top-start"
          enterDelay={TOOLTIP_APPEAR_DELAY}
          enterNextDelay={TOOLTIP_APPEAR_DELAY}
          title={`${i18n.t('paymentList.cardActions.print')}`}
        >
          <IconButton
            aria-label="Open"
            color="primary"
            onClick={() => window.open(printAnonECheckUrl(item.id), '_blank')}
          >
            <Printer variant="TwoTone" size="24" />
          </IconButton>
        </Tooltip>
      )}
      {!canUserDownloadCheck && (
        <Tooltip
          placement="top-start"
          enterDelay={TOOLTIP_APPEAR_DELAY}
          enterNextDelay={TOOLTIP_APPEAR_DELAY}
          title={`${i18n.t('paymentList.cardActions.printDisabled')}`}
        >
          {/*@ts-ignore */}
          <IconButton aria-label="Open" color="disabled">
            <PrinterSlash variant="TwoTone" size="24" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export const UserPaymentList = () => {
  const { t } = useTranslation();
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { organizationId } = useParams();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');

  const ref = useRef<HTMLInputElement>(null);
  const items = useSelector(paymentSelectors.paymentList);
  const allPaymentLoaded = useSelector(paymentSelectors.allPaymentsLoaded);
  const paymentFilters = useSelector(paymentSelectors.paymentFilters);
  const loadPaymentsPending = useSelector(
    paymentSelectors.loadUserPaymentsPending,
  );
  const loadMoreUserPaymentsPending = useSelector(
    paymentSelectors.loadMoreUserPaymentsPending,
  );
  const paymentsListLoaded = useSelector(paymentSelectors.paymentsListLoaded);

  const {
    loadUserPayments,
    loadUserMorePayments,
    updatePaymentFilters,
    resetPaymentFilters,
    clearPaymentList,
  } = usePayment();

  useEffect(() => {
    loadUserPayments(query);
  }, [paymentFilters, loadUserPayments, organizationId, query]);

  useEffect(() => {
    return () => {
      clearPaymentList();
      setQuery('');
    };
  }, [clearPaymentList]);

  const navigate = useNavigate();

  const onCardClick = useCallback(
    (item: APIPayment) => {
      if (organizationId) {
        navigate(userPaymentOrganizationDetailsUrl(organizationId, item.id));
        return;
      }

      navigate(userPaymentDetailsUrl(item.id));
    },
    [navigate, organizationId],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchInputChange = useCallback(
    _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        updatePaymentFilters({
          ...paymentFilters,
        });

        setQuery(event.target.value);
      }
    }, USER_INPUT_DEBOUNCE_TIME_MS),
    [updatePaymentFilters, paymentFilters],
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const bottom =
        Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
        1;

      if (bottom && !loadMoreUserPaymentsPending && !allPaymentLoaded) {
        loadUserMorePayments(query);
      }
    },
    [
      allPaymentLoaded,
      loadMoreUserPaymentsPending,
      loadUserMorePayments,
      query,
    ],
  );

  const onFilterReset = useCallback(() => {
    resetPaymentFilters();
    setQuery('');
    if (ref.current) {
      ref.current.value = '';
    }
  }, [resetPaymentFilters]);

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <Box width="100%" overflow="auto" onScroll={handleScroll}>
      <Box
        position="sticky"
        top={0}
        zIndex={11}
        bgcolor="white"
        alignItems="center"
      >
        <PageHeader
          header={t('userPaymentList.header')}
          subHeader={t('userPaymentList.subHeader')}
          icon={<Card variant="TwoTone" color={palette.primary900} size="28" />}
        >
          <>
            {/* 
             TODO: Hide for now since they dont want to expose request payment  functionality 07/17/2025
            <Button
              variant="text"
              onClick={() => setIsModalOpen(true)}
              startIcon={<PaymentsOutlined />}
            >
              {!isMobile && t('userPayment.requestPayment')}
            </Button> */}
            {isFilteringOn && (
              <Button variant="text" onClick={onFilterReset}>
                <CloseCircle variant="TwoTone" size="24" />
              </Button>
            )}
            <Button variant="text" onClick={() => setIsFilteringOn(it => !it)}>
              {isFilteringOn ? (
                <Filter variant="TwoTone" size="24" />
              ) : (
                <FilterRemove variant="TwoTone" size="24" />
              )}
            </Button>
          </>
        </PageHeader>

        {isFilteringOn && (
          <Box
            display="flex"
            alignItems="center"
            gap="0.5rem"
            padding="1rem 1rem 0 0 "
          >
            <SearchIcon color={palette.primary900} size="20" />
            <StyleInput
              inputRef={ref}
              autoComplete="off"
              autoCorrect="off"
              id="input-with-sx"
              onChange={onSearchInputChange}
              variant="standard"
            />
          </Box>
        )}
      </Box>
      {(!paymentsListLoaded || loadPaymentsPending) && (
        <Box
          paddingTop="1rem"
          display="grid"
          gap="1.25rem"
          sx={{
            overflow: 'hidden',
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(100%, 17rem), 1fr))',
          }}
        >
          {Array.from({ length: 8 }, (_, index) => (
            <Box
              key={`skeleton-${index}`}
              width="100%"
              height="15rem"
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <ContentLoader
                speed={2}
                width={272}
                height={240}
                viewBox="0 0 272 240"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                {/* Header section with avatar and title */}
                <circle cx="40" cy="40" r="20" />
                <rect x="70" y="25" rx="4" ry="4" width="120" height="12" />
                <rect x="70" y="45" rx="4" ry="4" width="80" height="10" />

                {/* Status chip */}
                <rect x="200" y="30" rx="8" ry="8" width="50" height="20" />

                {/* Content section with labels */}
                <rect x="20" y="80" rx="4" ry="4" width="100" height="12" />
                <rect x="20" y="102" rx="4" ry="4" width="120" height="12" />
                <rect x="20" y="124" rx="4" ry="4" width="90" height="12" />
                <rect x="20" y="146" rx="4" ry="4" width="140" height="12" />

                {/* Action area */}
                <rect x="200" y="190" rx="4" ry="4" width="40" height="32" />
              </ContentLoader>
            </Box>
          ))}
        </Box>
      )}
      {paymentsListLoaded && !loadPaymentsPending && (
        <Box
          paddingTop="1rem"
          display="grid"
          gap="1.25rem"
          sx={{
            overflow: 'hidden',
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(100%, 17rem), 1fr))',
          }}
        >
          {items?.map(it => (
            <CardBox<APIPayment>
              item={it}
              key={it.id}
              avatarUrl={
                it.organization
                  ? getOrganizationAvatarUrl(it.organization as any)
                  : undefined
              }
              title={it.payee.nameOnCheck ?? ''}
              width="100%"
              height="15rem"
              subTitle={it.organization.name}
              onClick={onCardClick}
              cardActions={cardActions}
              highlightQuery={query}
            >
              <CardBoxLabel
                color={palette.primary900}
                label={moneyFormatter(it.amount)}
                icon={DollarCircle}
                highlightQuery={query}
              />
              <CardBoxLabel
                color={palette.primary900}
                label={dayjs(it.checkDate).format('MM/DD/YYYY')}
                icon={Calendar}
                highlightQuery={query}
              />
              <CardBoxLabel
                color={palette.primary900}
                label={it.checkNumber}
                icon={Card}
                highlightQuery={query}
              />
              <CardBoxLabel
                color={palette.primary900}
                label={`${it.payee.city}, ${it.payee.address}, ${it.payee.address}`}
                icon={Home2}
                highlightQuery={query}
              />
            </CardBox>
          ))}
          {!items?.length && paymentsListLoaded && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <EmptyState
                showResetFilterButton={isFilteringOn}
                onFilterReset={onFilterReset}
              />
            </Box>
          )}
        </Box>
      )}
      {loadMoreUserPaymentsPending && (
        <Box display="flex" justifyContent="space-around" padding="1rem">
          <CircularProgress />
        </Box>
      )}
      {isModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '33rem'}
          height="27rem"
          onClose={onModalClose}
        >
          <PaymentRequestForm onModalClose={onModalClose} />
        </ModalBox>
      )}
    </Box>
  );
};
